/** @jsxImportSource @emotion/react */

import { FC, useEffect, useMemo, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import TabsNavigation from 'components/TabsNavigation'
import { FIND_ALL_GAME_CHALLENGES } from 'queries/gameChallenges'
import { findAllGameChallenge, findAllGameChallengeVariables } from '__generatedTypes__/findAllGameChallenge'
import Loader from 'components/Loader'
import { TabsItem } from 'types/tabsItem'
import { GameChallengeTypeEnum } from '__generatedTypes__/globalTypes'
import * as classes from './styles'
import { Form } from './Form'

const GAME_TITLE_BY_TYPE: Record<GameChallengeTypeEnum, string> = {
  [GameChallengeTypeEnum.AngleRankingChallenge]: 'Angle Ranking',
  [GameChallengeTypeEnum.BiologyChallenge]: 'Biology',
  [GameChallengeTypeEnum.CubeCountingChallenge]: 'Cube Counting',
  [GameChallengeTypeEnum.DiagnosisDisordersChallenge]: 'Diagnosis Disorders',
  [GameChallengeTypeEnum.FieldsOfDentistryChallenge]: 'Fields Of Dentistry',
  [GameChallengeTypeEnum.HolePunchingChallenge]: 'Hole Punching',
  [GameChallengeTypeEnum.OrganicChemistryChallenge]: 'Organic Chemistry',
  [GameChallengeTypeEnum.ReactionQBankChallenge]: 'Organic Chemistry Reaction',
  [GameChallengeTypeEnum.GeneralChemistryChallenge]: 'General Chemistry',
  [GameChallengeTypeEnum.PharmacologyChallenge]: 'Pharmacology',
  [GameChallengeTypeEnum.QuantitativeReasoningChallenge]: 'Quantitative Reasoning',
  [GameChallengeTypeEnum.TypingTestChallenge]: 'Typing Test',
  [GameChallengeTypeEnum.PathologyRadiologyChallenge]: 'Pathology Radiology',
  [GameChallengeTypeEnum.OralMedicinePathologyChallenge]: 'Oral Medicine & Pathology',
}

type Props = {
  platformId: string
}

export const Description: FC<Props> = ({ platformId }) => {
  const [activeTab, setActiveTab] = useState<TabsItem | null>(null)
  const [getGames, { data, loading }] = useLazyQuery<findAllGameChallenge, findAllGameChallengeVariables>(
    FIND_ALL_GAME_CHALLENGES,
    {
      variables: {
        query: {
          where: {
            platform: {
              id: platformId,
            },
          },
        },
      },
      fetchPolicy: 'cache-and-network',
    }
  )

  useEffect(() => {
    ;(async () => {
      try {
        const { data: games } = await getGames()

        if (games?.findAllGameChallenge?.items?.length) {
          setActiveTab({
            id: games.findAllGameChallenge.items[0].id,
            title: games.findAllGameChallenge.items[0].type,
          })
        }
      } catch (error) {
        console.error('An error occurred while trying to fetch games', error)
      }
    })()
  }, [])

  const games = data?.findAllGameChallenge?.items || []

  const selectedGame = useMemo(() => games.find((item) => item.id === activeTab?.id), [games, activeTab])

  const tabs = useMemo(
    () => games.map((item) => ({ id: item.id, title: GAME_TITLE_BY_TYPE[item.type], description: item.description })),
    [games]
  )

  const renderTab = () => (
    <Form
      key={selectedGame?.id}
      gameId={selectedGame?.id || ''}
      initialValues={{ description: selectedGame?.description || '' }}
    />
  )

  if (loading || !activeTab) {
    return <Loader />
  }

  if (!tabs.length) {
    return <p>No games found</p>
  }

  return (
    <section>
      <TabsNavigation<TabsItem> tabs={tabs} value={activeTab} onChange={setActiveTab} css={classes.tabs} />

      {renderTab()}
    </section>
  )
}
